import React, { useReducer } from "react";
import translate from "@/i18n/translate";
import { closeFlyout } from "../../../static/js/commons";
import { WmButton } from "@watermarkinsights/ripple-react";
import { useIntl } from "react-intl";
import { ProgramAndPageViews } from "./TopProgramsByCatalog";

export interface DrilldownViewProps {
  setIsDrilldownOpen: (arg0: boolean) => void;
  programDetails: ProgramAndPageViews[];
}

interface AllProgramsState {
  sortOrder: string;
}

interface AllProgramsAction {
  type: string;
  sortOrder: string;
}
const allProgramsAction = {
  type: "",
  sortOrder: ""
};

const initialState = {
  sortOrder: "desc"
};

const reducer = (state: AllProgramsState, action: AllProgramsAction) => {
  switch (action.type) {
    case "toggleSortingOrder":
      return {
        ...state,
        sortOrder: state.sortOrder === "asc" ? "desc" : "asc"
      };
    default:
      return state;
  }
};

export default function AllProgramsFlyout({
  setIsDrilldownOpen,
  programDetails
}: DrilldownViewProps) {
  const closeDrillDownView = () => {
    setIsDrilldownOpen(false);
    closeFlyout();
  };

  const closeTooltip = useIntl().formatMessage({
    id: "app.ces.responseRate.drilldownTooltipClose"
  });

  const [state, dispatch] = useReducer(reducer, initialState);

  const toggleSortingOrder = () => {
    dispatch({ ...allProgramsAction, type: "toggleSortingOrder" });
  };

  return (
    <div
      className="flyout-panel half-flyout show"
      role="dialog"
      id="drill-down-view"
      tabIndex={-1}
      aria-label="All Programs flyout "
    >
      <div className="overlay"></div>
      <div className="container-box all-programs-container">
        <div className="paper-card">
          <div className="overlay-content">
            <div className="helper-text">
              <h2 className="title">
                {translate("app.strategy.topProgramsByCatalog.flyoutHeading")}
              </h2>
              <div className="description" id="all-programs-description">
                {translate(
                  "app.strategy.topProgramsByCatalog.flyoutDescription"
                )}
              </div>
            </div>
            <WmButton
              buttonType="navigational"
              icon="f156"
              tooltip={closeTooltip}
              tooltipPosition="bottom"
              id="all-programs-close-btn"
              class="hydrated"
              iconSize="18px"
              onClick={closeDrillDownView}
            />
          </div>
        </div>
        <section id="org-table-catalog" className="all-programs-table">
          <Table>
            <TableHeader
              columns={[
                translate("app.strategy.topProgramsByCatalog.program"),
                translate("app.strategy.topProgramsByCatalog.pageViews")
              ]}
              toggleSortingOrder={toggleSortingOrder}
              sortOrder={state.sortOrder}
            />
            <TableBody
              programDetails={programDetails}
              sortOrder={state.sortOrder}
            />
          </Table>
        </section>
      </div>
    </div>
  );
}

interface TableBodyProps {
  programDetails: ProgramAndPageViews[];
  sortOrder: string;
}
const TableBody = ({ programDetails, sortOrder }: TableBodyProps) => {
  const sortedProgramDetails = [...programDetails].sort((a, b) => {
    if (sortOrder === "asc") {
      return a.pageViews - b.pageViews;
    } else {
      return b.pageViews - a.pageViews;
    }
  });
  return (
    <tbody className="databody">
      {sortedProgramDetails.map(
        (programDetail: ProgramAndPageViews, index: number) => {
          return (
            <tr
              className="datarow"
              key={`${index}-${programDetail.programName}`}
            >
              <td className="datacell">
                <div>{programDetail.programName}</div>
              </td>
              <td className="datacell">{programDetail.pageViews}</td>
            </tr>
          );
        }
      )}
    </tbody>
  );
};
const Table = (props: any) => {
  return (
    <table id="flyout-table" className="data-table -shadow">
      {props.children}
    </table>
  );
};

interface TableHeaderProps {
  columns: Array<any>;
  toggleSortingOrder: () => void;
  sortOrder: string;
}
export const TableHeader = (props: TableHeaderProps) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowUp") {
      props.toggleSortingOrder();
    } else if (e.key === "ArrowDown") {
      props.toggleSortingOrder();
    }
  };
  return (
    <thead className="dataheader">
      <tr className="dataheaderrow">
        <th className="dataheadercell" data-th={props.columns[0]}>
          <span>{props.columns[0]}</span>
        </th>

        <th
          className="sortable-table-header dataheadercell"
          data-th={props.columns[1]}
          onClick={() => props.toggleSortingOrder()}
          onKeyDown={handleKeyDown}
        >
          <div
            className="pointer-cursor"
            tabIndex={0}
            aria-sort={props.sortOrder === "asc" ? "ascending" : "descending"}
            role="columnheader"
          >
            <span>{props.columns[1]}</span>
            <div className="arrow-position">
              <span
                className={
                  props.sortOrder === "asc"
                    ? "mdi mdi-arrow-up sort-arrow"
                    : "mdi mdi-arrow-down sort-arrow"
                }
              ></span>
            </div>
          </div>
        </th>
      </tr>
    </thead>
  );
};
